import { Fragment, h } from 'preact';
import { decodeId, generateUrl, getProductLabel } from 'utils/helpers';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';

import clsx from 'clsx';
import { formatMoney } from 'utils/storefront/format-resource';
import useInView from 'utils/in-view';

import useUniqueId from 'utils/unique-id';
import useResponsive from 'utils/responsive';
import ProductPrice from './product-price';
import ProductStickyButton from './product-sticky-button';
import ProductSwatches from '../product-swatches/product-swatches';
import VariantSelector from './variant-selector';
import useDiscount from '../product-discounts/product-discount';

const AddToCartButton = ({
  currentVariant,
  currentProduct,
  form,
  isQuickView,
  isLookOverlay,
}) => {
  const handleAddToCart = (e) => {
    e.preventDefault();
    if (!currentVariant && !isQuickView) {
      form.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      return;
    }

    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload: {
          merchandiseId: currentVariant.id,
          quantity: 1,
          attributes: [
            {
              key: '_FR_Title',
              value: currentProduct?.fr_title?.value
                ? currentProduct.fr_title?.value
                : currentProduct.fr_title,
            },
            {
              key: '_FR_Color',
              value: currentProduct?.fr_color?.value
                ? currentProduct.fr_color?.value
                : currentProduct.fr_color,
            },
          ],
        },
      },
    });
    document.dispatchEvent(addToCartEvent);
  };
  const discount = useDiscount(
    currentProduct.id,
    currentVariant?.price?.amount,
    currentProduct.collectionsId,
  );
  const currentProductComingSoon =
    currentProduct.tags.filter((tag) => tag.toLowerCase() === 'coming-soon')
      .length > 0;

  return (
    <div
      className={clsx(
        'atc-container pt-16 pb-40 gap-16 flex flex-col lc:lg:py-20',
        {
          'absolute px-24 bottom-0 w-full h-[115px] ml:h-[117px] bg-white lc:border-t lc:border-t-grey-200 ml:shadow-[0_16px_34px_0px_rgba(0,0,0,0.25)] md:w-[507px] z-20':
            isQuickView,
          'ml:pt-20 ml:md:py-22 gap-24 ml:tablet:gap-22 lc:pb-0 lc:lg:pb-0 ml:pb-0 ml:lg:pb-0 lc:md:pt-24 lc:lg:pt-24 ml:md:pt-24':
            isLookOverlay,
        },
      )}
    >
      <button
        type="submit"
        className={`
          button button--primary
          w-full
          group
          ml:uppercase
          ${
            currentVariant || !currentProduct.availableForSale
              ? ''
              : 'hover:bg-black hover:text-white'
          }`}
        disabled={
          (currentVariant && !currentVariant.availableForSale) ||
          !currentProduct.availableForSale
        }
        onClick={(e) => handleAddToCart(e)}
      >
        {currentVariant ? (
          currentVariant?.availableForSale ? (
            `${window.TMW.Strings.product.addToCart} - ${formatMoney(
              discount?.discountAmount > 0
                ? discount.discountPrice
                : currentVariant.price,
              false,
            )}`
          ) : currentProductComingSoon ? (
            window.TMW.Strings.product.comingSoon
          ) : (
            window.TMW.Strings.product.outOfStock
          )
        ) : (
          <>
            {currentProduct.availableForSale ? (
              <>
                <div className="group-hover:hidden">
                  {window.TMW.Strings.product.addToCart}
                </div>
                <div className="hidden group-hover:block">
                  {window.TMW.Strings.product.selectSize}
                </div>
              </>
            ) : currentProductComingSoon ? (
              window.TMW.Strings.product.comingSoon
            ) : (
              window.TMW.Strings.product.outOfStock
            )}
          </>
        )}
      </button>
      <div
        className={clsx('flex justify-center', {
          'items-center gap-40': isQuickView,
        })}
      >
        {isQuickView && (
          <a
            href={`${Shopify.routes.root}products/${currentProduct.handle}`}
            className="body-3-underline flex-none"
          >
            {window.TMW.Strings.product.viewDetails}
          </a>
        )}
        <button
          type="button"
          className={clsx(
            'swym-button swym-add-to-wishlist-view-product !block pointer-events-none [&.swym-loaded]:!pointer-events-auto group !h-auto !p-0 !normal-case [&.disabled]:!opacity-100 [&>span]:!body-3-underline  [&:hover>span]:!no-underline flex-none !leading-100',
            `product_${decodeId(currentProduct.id, 'Product')}`,
          )}
          data-with-epi="true"
          data-swaction="addToWishlist"
          data-product-id={decodeId(currentProduct.id, 'Product')}
          data-variant-id={decodeId(
            currentProduct?.variants?.[0].id,
            'ProductVariant',
          )}
          data-product-url={generateUrl(currentProduct.handle, 'products')}
        >
          <span className="!text-default group-[.swym-added]:hidden">
            {window.TMW.Strings.product.addToWishlist}
          </span>
          <span className="!text-default hidden group-[.swym-added]:!block">
            {window.TMW.Strings.product.addedToWishlist}
          </span>
        </button>
      </div>
    </div>
  );
};

const ProductForm = ({
  currentProduct: product,
  currentVariant,
  products,
  relatedProducts,
  onProductChange,
  setCurrentVariant,
  isQuickView = false,
  isLookOverlay = false,
  galleryInView,
  options,
  setOptions,
}) => {
  const [formContainer, formInView] = useInView();
  const [footerInView, setFooterInView] = useState(false);

  const form = useRef(null);
  const inputPrefix = useUniqueId();
  const { isMobile } = useResponsive();
  const label = getProductLabel(product.tags || []);
  const hasOnlyDefaultVariant = useMemo(
    () =>
      product.hasOnlyDefaultVariant ||
      (product.options &&
        product.options.every((option) => option.values?.length === 1)),
    [product],
  );

  const productHasFit = useMemo(
    () =>
      product.options &&
      product.options.find(
        (option) =>
          option.name.trim().toLowerCase() ===
          window.TMW.Strings.product.options.fit,
      ) &&
      product.options
        .find(
          (option) =>
            option.name.trim().toLowerCase() ===
            window.TMW.Strings.product.options.fit,
        )
        .values?.find((value) => value !== 'Melanie Lyne'),
    [product.options],
  );

  useEffect(() => {
    if (!isQuickView && !isLookOverlay) {
      const searchParams = new URLSearchParams(window.location.search);
      if (currentVariant) {
        const id = currentVariant?.id.split('/ProductVariant/')[1];
        searchParams.set('variant', id);
      }
      const newRelativePathQuery = `${Shopify.routes.root}products/${
        product.handle
      }?${searchParams.toString()}${window.location.hash}`;
      window.history.replaceState(null, '', newRelativePathQuery);
    }
  }, [currentVariant, product]);

  useEffect(() => {
    if (!hasOnlyDefaultVariant) {
      const currentOptions = options.reduce(
        (items, item) => ({
          ...items,
          ...{
            [item.name]: item.value,
          },
        }),
        {},
      );
      const newVariant = product.variants.find((v) =>
        v.selectedOptions.every(
          (opt) => currentOptions[opt.name] === opt.value,
        ),
      );
      setCurrentVariant(newVariant || null);
      if (newVariant && !isQuickView && !isLookOverlay) {
        document.dispatchEvent(
          new CustomEvent('productUpdated', {
            detail: product,
          }),
        );
      }
    } else {
      setCurrentVariant({
        ...product.variants[0],
      });
    }
  }, [options]);

  const isStickyVisible = useMemo(() => {
    if (isMobile) {
      return !formInView && !galleryInView && !footerInView;
    }
    return !formInView && !footerInView;
  }, [formInView, galleryInView, footerInView, isMobile]);

  const variantSelectors = useMemo(
    () =>
      !product.hasOnlyDefaultVariant && (
        <div
          className={
            isQuickView
              ? 'px-24 overflow-auto hide-scrollbar'
              : isLookOverlay
              ? 'flex flex-col items-end lc:mb-8 ml:mb-4 lc:md:mb-0 ml:md:mb-0 md:min-h-20 lc:md:min-h-[75px]'
              : ''
          }
        >
          {product.options.map((option) => (
            <VariantSelector
              key={option.name}
              inputPrefix={inputPrefix}
              option={option}
              product={product}
              relatedProducts={relatedProducts}
              isQuickView={isQuickView}
              isLookOverlay={isLookOverlay}
              options={options}
              setOptions={setOptions}
            />
          ))}
        </div>
      ),
    [product, options, relatedProducts],
  );

  const price = useMemo(
    () => (
      <ProductPrice
        className={clsx(
          '[&_.price]:body-1 ml:text-center flex flex-wrap gap-x-4 items-center',
          {
            'ml:justify-center': !isLookOverlay,
          },
        )}
        saleClass="text-misc-sale body-3"
        compareClass="body-3 text-subdued"
        showRange={false}
        variant={currentVariant}
        product={product}
      />
    ),
    [currentVariant, product],
  );

  const addToCartButton = useMemo(
    () => (
      <AddToCartButton
        currentVariant={currentVariant}
        currentProduct={product}
        form={form}
        isQuickView={isQuickView}
        isLookOverlay={isLookOverlay}
      />
    ),
    [currentVariant, form, product],
  );

  // used for hiding sticky ATC when footer is visible
  const intersectionObserver = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      setFooterInView(true);
    } else {
      setFooterInView(false);
    }
  });
  const footer = document.querySelector('footer');
  intersectionObserver.observe(footer);

  return (
    <>
      <div
        className={clsx({
          'pb-[115px] ml:pb-[117px]': isQuickView,
          'flex flex-col items-end': isLookOverlay,
        })}
        ref={formContainer}
      >
        <div
          className={clsx('flex flex-col items-end', {
            'lc:min-h-182 ml:min-h-168 lc:md:min-h-[234px] ml:md:min-h-164':
              isLookOverlay && !productHasFit,
            'lc:min-h-182 ml:min-h-168 lc:md:min-h-0 ml:md:min-h-0':
              isLookOverlay && productHasFit,
            'w-full lc:md:max-w-271 ml:md:max-w-332 lc:max-w-[calc(100%_-_138px)] ml:max-w-[calc(100%_-_127px)]':
              isLookOverlay,
          })}
        >
          <div
            className={clsx('w-full', {
              'ml:text-center p-24': isQuickView,
              'ml:text-center pt-24 lg:pt-0 ml:pt-32 ml:lg:pt-0 ml:pb-16 lc:lg:pb-20':
                !isQuickView && !isLookOverlay,
              'ml:pb-16 lc:pb-24': isLookOverlay,
            })}
          >
            {isLookOverlay ? (
              <h3 className="!normal-case pb-8">
                <a href={generateUrl(product.handle, 'products')}>
                  {product.title}
                </a>
              </h3>
            ) : (
              <h1 className="display-4 !normal-case pb-4 lg:pb-10">
                {product.title}
              </h1>
            )}
            <div
              className={clsx('flex items-center gap-x-8 flex-wrap', {
                'ml:justify-center': !isLookOverlay,
              })}
            >
              {price}
              {!isQuickView && !isLookOverlay && !!label && (
                <div className="body-3 text-misc-sale">{label}</div>
              )}
            </div>
          </div>
          <ProductSwatches
            currentProduct={product}
            products={products}
            isQuickView={isQuickView}
            isLookOverlay={isLookOverlay}
            onChange={onProductChange}
            options={options}
          />
        </div>
        <form
          method="post"
          action="/cart/add"
          acceptCharset="UTF-8"
          encType="multipart/form-data"
          noValidate="novalidate"
          ref={form}
          data-product-form
          className={clsx({
            '[&_.swym-inject]:hidden swym-wishlist-button-bar ': !isLookOverlay,
            '[&_.swym-inject]:hidden swym-wishlist-button-bar w-full':
              isLookOverlay,
          })}
        >
          {variantSelectors}
          {addToCartButton}
        </form>
      </div>

      {!isQuickView && !isLookOverlay && (
        <ProductStickyButton
          product={product}
          variant={currentVariant}
          options={options}
          isVisible={isStickyVisible}
        >
          {addToCartButton}
        </ProductStickyButton>
      )}
    </>
  );
};
export default ProductForm;
